import * as React from 'react';
import { styled } from '@compiled/react';
import { Button } from '@helpcenter/analytics-components';
import { bp } from '@helpcenter/styles/src/compiled/breakpoints';
import * as grid from '@helpcenter/styles/src/grid';
import { injectIntl } from 'react-intl';
import type { WrappedComponentProps } from 'react-intl';
import { ButtonGroup } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';
import messages from './messages';
import type { ActionBarWithButtonsStatelessProps } from './types';

export const ActionsContent: React.FC<ActionBarWithButtonsStatelessProps & WrappedComponentProps> = ({
    intl,
    isDirty,
    openCancelPrompt,
    openPublishPrompt,
}) => {
    return (
        <SpotlightTarget name="portal-reordering-action-buttons">
            <ButtonContainer>
                <ButtonGroup>
                    <Button
                        appearance="subtle"
                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
                        className="portal-reordering-cancel-button"
                        onClick={openCancelPrompt}
                        actionSubjectId="cancelPortalsReorder"
                        data-test-id="cancel-portal-reorder-experience-action-bar"
                    >
                        {intl.formatMessage(messages.cancelButtonLabel)}
                    </Button>
                    <Button
                        appearance="primary"
                        isDisabled={!isDirty}
                        onClick={openPublishPrompt}
                        actionSubjectId="publishPortalsReorder"
                        data-test-id="publish-portal-reorder-experience-action-bar"
                    >
                        {intl.formatMessage(messages.publishButtonLabel)}
                    </Button>
                </ButtonGroup>
            </ButtonContainer>
        </SpotlightTarget>
    );
};

export class ActionBarWithButtonsStateless extends React.Component<
    React.PropsWithChildren<ActionBarWithButtonsStatelessProps & WrappedComponentProps>
> {
    renderHeading() {
        const { intl } = this.props;

        if (isCSMHelpCenter(getEnv().helpCenterType)) {
            return intl.formatMessage(messages.actionBarTitleCSM);
        }
        return intl.formatMessage(messages.actionBarTitleNew);
    }

    render() {
        const { children } = this.props;

        return (
            <Wrapper>
                <PageHeaderWrapper>
                    <PageHeader>
                        <Title>
                            <Heading size="medium" as="div">
                                {this.renderHeading()}
                            </Heading>
                        </Title>
                        <ActionsContent {...this.props} />
                    </PageHeader>
                    {children}
                </PageHeaderWrapper>
            </Wrapper>
        );
    }
}
export default injectIntl(ActionBarWithButtonsStateless);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Wrapper = styled.header({
    boxShadow: token(
        'elevation.shadow.overlay',
        `0 8px 16px -4px ${'rgba(9, 30, 66, 0.25)'}, 0 0 1px ${'rgba(9, 30, 66, 0.31)'}`
    ),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    zIndex: layers.modal(),
    top: 0,
    left: 0,
    width: '100%',
    position: 'fixed',
    backgroundColor: token('elevation.surface.overlay', '#FFFFFF'),
    display: 'flex',
    justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const PageHeaderWrapper = styled.div({
    width: '100%',
    justifyContent: 'center',
    // Upgrading @atlaskit/platform; fix these violations.
    // eslint-disable-next-line @atlaskit/platform/expand-spacing-shorthand
    padding: `0 ${token('space.500', '40px')}`,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    [bp.from.fromLarge]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
        maxWidth: grid.multiple(145).px,
    },
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ButtonContainer = styled.div({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    maxWidth: grid.multiple(96).px,
    // Upgrading @atlaskit/platform; fix these violations.
    // eslint-disable-next-line @atlaskit/platform/expand-spacing-shorthand
    padding: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Title = styled.div({
    marginTop: 0,
    marginBottom: token('space.200', '16px'),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    [bp.from.fromMobile]: {
        marginBottom: 0,
    },
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const PageHeader = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Upgrading @atlaskit/platform; fix these violations.
    // eslint-disable-next-line @atlaskit/platform/expand-spacing-shorthand
    margin: `${token('space.250', '20px')} 0`,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    [bp.from.fromMobile]: {
        flexDirection: 'row',
    },
});
